import { useLocalStorage } from "@mantine/hooks";
import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
  MRT_VisibilityState
} from "mantine-react-table-open";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import { usePageParams } from "../../../hooks/usePageParams";
import type { RevenueTabs, TableTabs } from "../RevenueMonitoring.types";

function useEnergyRevenuesPageParams() {
  const [searchParams] = useSearchParams();
  const { useURLParam, useJSONParam, updateURL } = usePageParams();

  // Simple URL parameters
  const [tab, setTab] = useURLParam<RevenueTabs>("tab");
  const [tableTab, setTableTab] = useURLParam<TableTabs>(
    "tableTab",
    "aktuelle-erloese"
  );
  const [verificationDetailId, setVerificationDetailIdState] =
    useURLParam<string>("verificationDetailId", "");

  // Company ID with localStorage fallback
  const [companyIdLS, setCompanyIdLS] = useLocalStorage<string>({
    key: "EnergyRevenueSelectedCompanyId",
    getInitialValueInEffect: false
  });

  const [companyId, setCompanyIdState] = useState<number | undefined>(() => {
    const urlValue = searchParams.get("companyId");
    if (urlValue) {
      return Number(urlValue);
    }
    if (companyIdLS) {
      return Number(companyIdLS);
    }
    return undefined;
  });

  const setCompanyId = useCallback(
    (id: number) => {
      setCompanyIdState(id);
      setCompanyIdLS(String(id));
      updateURL("companyId", String(id));
    },
    [setCompanyIdLS, updateURL]
  );

  // Generator IDs with localStorage fallback
  const [selectedGeneratorsLS, setSelectedGeneratorsLS] = useLocalStorage<
    Array<string>
  >({
    key: "EnergyRevenueSelectedGenerators",
    defaultValue: []
  });

  const [generatorIds, setGeneratorIdsState] = useState<
    Array<string> | undefined
  >(() => {
    const urlValue = searchParams.get("generatorIds");
    if (urlValue) {
      return urlValue.split(",");
    }
    if (selectedGeneratorsLS.length > 0) {
      return selectedGeneratorsLS;
    }
    return [];
  });

  const setGeneratorIds = useCallback(
    (ids: Array<string>) => {
      setGeneratorIdsState(ids);
      setSelectedGeneratorsLS(ids);
      updateURL("generatorIds", ids.join(","));
    },
    [setSelectedGeneratorsLS, updateURL]
  );

  // JSON parameters for table state
  const [sort, setSort] = useJSONParam<MRT_SortingState>("sort", []);
  const [verificationFilters, setVerificationFilters] =
    useJSONParam<MRT_ColumnFiltersState>("verificationFilters", []);
  const [columnVisibility, setColumnVisibility] =
    useJSONParam<MRT_VisibilityState>("columnVisibility", {});
  const [verificationSort, setVerificationSort] =
    useJSONParam<MRT_SortingState>("verificationSort", []);
  const [filters, setFilters] = useJSONParam<MRT_ColumnFiltersState>(
    "filters",
    []
  );
  const [verificationColumnVisibility, setVerificationColumnVisibility] =
    useJSONParam<MRT_VisibilityState>("verificationColumnVisibility", {});

  // Special setter for verification detail view
  const setVerificationDetailView = useCallback(
    (id: string) => {
      setVerificationDetailIdState(id);
      setTableTab("pruefdetail");
    },
    [setTableTab, setVerificationDetailIdState]
  );

  return {
    tab,
    setTab,
    tableTab,
    setTableTab,
    companyId,
    setCompanyId,
    generatorIds,
    setGeneratorIds,
    sort,
    setSort,
    filters,
    setFilters,
    columnVisibility,
    setColumnVisibility,
    verificationSort,
    setVerificationSort,
    verificationFilters,
    setVerificationFilters,
    verificationColumnVisibility,
    setVerificationColumnVisibility,
    verificationDetailId,
    setVerificationDetailView
  };
}

export { useEnergyRevenuesPageParams };
